import React from "react";
import type { GroupBase, MultiValue, OptionsOrGroups, StylesConfig } from "react-select";
import AsyncSelect, { type AsyncProps } from "react-select/async";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faCaretDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Popover from "@radix-ui/react-popover";
import cx from "classnames";
import { noop, throttle } from "lodash";
import useResizeObserver from "use-resize-observer";

import { nGetText } from "~/lib/utils";

import {
	defaultGetOptionValue,
	IndicatorsContainer,
	Input,
	type MultiSelectorOption,
	Option,
	Placeholder,
	reactSelectStylesRoot,
	sortSelectedToTop,
	ValueContainer,
} from "./MultiSelectorUtils";

import styles from "./MultiSelector.module.css";

declare module "csstype" {
	interface Properties {
		"--selector-width"?: string;
	}
}

export interface AsyncMultiSelectorProps<T>
	extends Omit<AsyncProps<T, true, GroupBase<T>>, "menuPlacement" | "menuPortalTarget"> {
	/** If true, selected items will be sorted to the top of the flyout list */
	sortSelected?: boolean;
	/** Use smaller "condensed" style */
	condensed?: boolean;
	width?: string;
	valueComponent?: React.ReactNode;
	/** What direction the flyout menu should open
	 * @see {@link https://www.radix-ui.com/primitives/docs/components/popover#content | Radix UI docs}
	 */
	menuSide?: Popover.PopoverContentProps["side"];
	/** Which side of the flyout menu should be aligned with the trigger
	 * @see {@link https://www.radix-ui.com/primitives/docs/components/popover#content | Radix UI docs}
	 */
	menuAlign?: Popover.PopoverContentProps["align"];
	/** Disable this selector
	 * @privateRemarks This is re-declared here so it shows up in Storybook
	 */
	isDisabled?: boolean;
	/** Max width of the menu flyout as a CSS value */
	menuMaxWidth?: string;
}

export default function AsyncMultiSelector<T extends MultiSelectorOption>({
	cacheOptions,
	className,
	components: customComponents,
	condensed,
	defaultOptions,
	placeholder,
	sortSelected,
	value,
	width,
	valueComponent,
	loadOptions,
	getOptionValue = defaultGetOptionValue,
	menuSide,
	menuAlign,
	menuMaxWidth,
	isClearable = true,
	...rest
}: AsyncMultiSelectorProps<T>) {
	const { isDisabled, onChange } = rest;

	let reactSelectStyles = reactSelectStylesRoot as unknown as StylesConfig<T, true>;

	const [isOpen, setIsOpen] = React.useState(false);
	const [contentWidth, setContentWidth] = React.useState("auto");

	const buttonRef = React.useRef<HTMLButtonElement>(null);
	useResizeObserver<HTMLButtonElement>({
		ref: buttonRef,
		onResize: React.useMemo(
			() =>
				throttle(({ width }) => {
					if (!width || condensed) return;
					setContentWidth(`${width}px`);
				}, 200),
			[condensed],
		),
	});

	const toggleOpen = React.useCallback(() => setIsOpen(isOpen => !isOpen), []);

	const toggleArrowKeyDown = React.useCallback(
		(event: React.KeyboardEvent<HTMLButtonElement>) => {
			if (event.key === "ArrowDown") toggleOpen();
			if (event.key === "Enter") event.preventDefault();
		},
		[toggleOpen],
	);

	const toggleEscape = React.useCallback(() => {
		setIsOpen(false);
		buttonRef.current?.focus();
	}, [buttonRef]);

	const values: MultiValue<T> = React.useMemo(() => (value ? ("length" in value ? value : [value]) : []), [value]);

	const sortLoadOptions = React.useCallback(
		(inputValue: string, callback: (options: OptionsOrGroups<T, GroupBase<T>>) => void) => {
			if (!loadOptions) return;

			loadOptions(inputValue, options => {
				const sorted = sortSelectedToTop(options ?? [], values, getOptionValue, sortSelected ?? false);
				callback(sorted);
			});
		},
		[getOptionValue, loadOptions, sortSelected, values],
	);

	const clearSelection = React.useCallback(
		(event: React.SyntheticEvent) => {
			event.stopPropagation();
			if (onChange) onChange([], { action: "clear", removedValues: values });
		},
		[values, onChange],
	);

	return (
		<Popover.Root open={isOpen} onOpenChange={isDisabled ? noop : toggleOpen}>
			<Popover.Trigger asChild>
				<button
					className={cx(className, condensed ? styles.targetCondensed : styles.targetOutline, {
						[condensed ? styles.targetCondensedActive : styles.targetOutlineActive]: isOpen && !isDisabled,
						[condensed ? styles.targetCondensedDisabled : styles.targetOutlineDisabled]: isDisabled,
					})}
					onKeyDown={toggleArrowKeyDown}
					style={{ "--selector-width": width ?? (condensed ? "auto" : "100%") }}
					ref={buttonRef}
				>
					{values.length === 0 ? (
						<div className={styles.targetPlaceholder}>{placeholder}</div>
					) : (
						<div className={styles.targetValues}>
							{valueComponent ? (
								valueComponent
							) : condensed ? (
								`${values.length} ${nGetText(values.length, "item", "items")} selected`
							) : (
								<div className={styles.targetItem}>
									<div className={styles.targetText}>
										{values.length} {nGetText(values.length, "selection", "selections")}
									</div>
								</div>
							)}
						</div>
					)}
					{values.length > 0 && isClearable && (
						<div className={styles.targetDropdownIconContainer} onClick={clearSelection}>
							<FontAwesomeIcon icon={faTimes} className={styles.targetDropdownIcon} />
						</div>
					)}
					<div className={styles.targetDropdownIconContainer}>
						<FontAwesomeIcon icon={faCaretDown} className={styles.targetDropdownIcon} />
					</div>
				</button>
			</Popover.Trigger>
			<Popover.Portal>
				<Popover.Content
					sideOffset={2}
					style={{ width: contentWidth, maxWidth: menuMaxWidth }}
					onEscapeKeyDown={toggleEscape}
					className={styles.content}
					side={menuSide}
					align={menuAlign}
					data-ignore-focus-lock
				>
					<AsyncSelect<T, true>
						{...rest}
						defaultOptions={defaultOptions ?? true}
						isMulti
						isSearchable // By definition an async selector should be searchable
						menuIsOpen
						backspaceRemovesValue={false}
						cacheOptions={cacheOptions ?? true}
						components={{
							Option,
							...customComponents,
							IndicatorsContainer,
							Input,
							Placeholder,
							ValueContainer,
						}}
						controlShouldRenderValue={false}
						hideSelectedOptions={false}
						isClearable={false}
						tabSelectsValue={false}
						styles={reactSelectStyles}
						value={value}
						getOptionValue={getOptionValue}
						loadOptions={sortLoadOptions}
					/>
				</Popover.Content>
			</Popover.Portal>
		</Popover.Root>
	);
}

export { components } from "react-select";
export type {
	ActionMeta,
	GroupBase,
	MultiValue,
	OptionProps,
	PlaceholderProps,
	SelectComponentsConfig,
} from "react-select";
export type { MultiSelectorOption };
